import { useApp, useUserInfo, useUserSettings } from "@next-platform/client-common-core";
import { FetchStatus } from "@next-platform/client-common-core/user/userSettings/types";
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import Spinner from "@/components/customComponents/Spinner";
import ErrorSection from "@/components/ErrorSection/ErrorSection";
import { NSFW_TOGGLE_LOCALSTORAGE_KEY } from "@/constants";
import useAuth from "@/services/useAuth";
import { Collection } from "@/types/api";
import { getIsPartner } from "@/utils/getIsPartner";
import { typedLocalStorageService } from "@/utils/localStorageRe";

import DataSourceDropdown from "../DataSourceDropdown";
import { DataSourceType, DisplayContext, SearchListingConfig } from "../types";
import { MyChatbotsSearchProvider, useMyChatbotsSearch } from "./contexts/MyChatbotsSearchContext";
import { MyChatbotsListing, SharedChatbotsListing } from "./Listings";

interface IMembersListingProps {
  charactersList?: [];
  recommId?: string;
  useStoreTagFilters?: boolean;
  config?: SearchListingConfig;
  defaultDataSourceType?: DataSourceType;
  onDataSourceTypeChange?: (source: DataSourceType) => void;
  collection?: Collection;
}

const MembersListingContent = ({
  charactersList = [],
  recommId = "",
  useStoreTagFilters = true,
  config,
  defaultDataSourceType = DataSourceType.MY_CHATBOTS,
  onDataSourceTypeChange,
  collection = Collection.CHARACTERS,
}: IMembersListingProps) => {
  const { role } = useUserInfo();
  const { showNSFW, blockedUsers, areUserSettingLoading, likedBots } = useUserSettings();
  const { username } = useParams();
  const { isNsfwEnabled } = useApp();
  const nsfwToggleStorage = isNsfwEnabled ? typedLocalStorageService.getItem(NSFW_TOGGLE_LOCALSTORAGE_KEY) : null;
  const [stateIsNsfwToggle, setStateIsNsfwToggle] = useState(nsfwToggleStorage === "true");
  const { isAuthenticated, permissions } = useAuth();
  const isPartner = getIsPartner();

  const [stateDataSourceType, setStateDataSourceType] = useState<DataSourceType>(defaultDataSourceType);
  const [stateIsCanAutoSwitch, setStateIsCanAutoSwitch] = useState(true);
  const { chatbots, isLoading } = useMyChatbotsSearch();

  const handleDataSourceChange = useCallback(
    (source: DataSourceType) => {
      setStateDataSourceType(source);
      setStateIsCanAutoSwitch(false);
      if (onDataSourceTypeChange) {
        onDataSourceTypeChange(source);
      }
    },
    [onDataSourceTypeChange, setStateDataSourceType, setStateIsCanAutoSwitch],
  );

  useEffect(() => {
    if (nsfwToggleStorage === "true") {
      setStateIsNsfwToggle(true);
    }
  }, [nsfwToggleStorage]);

  useEffect(() => {
    // Auto-switch to SharedChatbots if no MyChatbots are found
    if (!isLoading && chatbots && chatbots.length === 0 && stateIsCanAutoSwitch && stateDataSourceType === DataSourceType.MY_CHATBOTS) {
      handleDataSourceChange(DataSourceType.SHARED_CHATBOTS);
    }
  }, [isLoading, chatbots, chatbots?.length, stateIsCanAutoSwitch, stateDataSourceType, handleDataSourceChange]);

  if (areUserSettingLoading === FetchStatus.failed) {
    return <ErrorSection shouldCenter={true} buttonText="Retry" />;
  }

  // Create custom filters component if needed
  const customFilters = config?.showDataSourceDropdown ? (
    <DataSourceDropdown currentSource={stateDataSourceType} onSourceChange={handleDataSourceChange} showDropdown={true} />
  ) : undefined;

  // Extract selection props for MEMBER_SELECT context
  const selectionProps =
    config?.context === DisplayContext.MEMBER_SELECT && "maxItemsToSelect" in config
      ? {
          onItemAction: config.onItemAction,
          selectedItems: config.selectedItems,
          maxItemsToSelect: config.maxItemsToSelect,
          onLimitReached: config.onLimitReached,
          dataSourceType: stateDataSourceType,
        }
      : {
          onItemAction: config?.onItemAction,
        };

  if (stateDataSourceType === DataSourceType.MY_CHATBOTS) {
    // Show loading spinner while fetching MyChatbots and  prevent flash
    if (isLoading || !chatbots || (chatbots.length === 0 && stateIsCanAutoSwitch)) {
      return (
        <div className="flex justify-center items-center w-full h-96">
          <Spinner />
        </div>
      );
    }

    // This component renders the list of MyChatbots
    // Unlike the SharedChatbotsListing, it doesn’t use Typesense, it fetches data from the API
    // For details, see useMyChatbots.ts
    return (
      <MyChatbotsListing
        nsfwToggle={stateIsNsfwToggle}
        setNsfwToggle={setStateIsNsfwToggle}
        showNSFW={showNSFW}
        isPartner={isPartner}
        config={config}
        recommId={recommId}
        permissions={permissions ?? []}
        customFilters={customFilters}
        dataSourceType={stateDataSourceType}
        {...selectionProps}
      />
    );
  }

  const effectiveCharactersList = stateDataSourceType === DataSourceType.FAVOURITES ? likedBots : charactersList;
  return (
    // This component renders the list of chatbots using Typesense as the data source.
    <SharedChatbotsListing
      collection={collection}
      charactersList={effectiveCharactersList}
      username={username}
      dataSourceType={stateDataSourceType}
      blockedUsers={blockedUsers}
      nsfwToggle={stateIsNsfwToggle}
      setNsfwToggle={setStateIsNsfwToggle}
      showNSFW={showNSFW}
      isPartner={isPartner}
      config={config}
      recommId={recommId}
      useStoreTagFilters={useStoreTagFilters}
      isAuthenticated={isAuthenticated}
      permissions={permissions ?? []}
      role={role}
      customFilters={customFilters}
      {...selectionProps}
    />
  );
};

const MembersListing = (props: IMembersListingProps) => (
  <MyChatbotsSearchProvider>
    <MembersListingContent {...props} />
  </MyChatbotsSearchProvider>
);

const MembersListingMemo = React.memo(MembersListing);

export default MembersListingMemo;
